import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const counterVariants = cva(
  "inline-flex items-center justify-center  transition-colors",
  {
    variants: {
      size: {
        small:
          "text-body-xsmall-medium rounded-small px-1 py-0.5 min-w-[20px] h-[16px]",
        medium:
          "text-body-small-medium rounded-medium px-2 py-1 min-w-[24px] h-[20px]",
        large:
          "text-body-medium-medium rounded-medium px-2 py-1 min-w-[28px] h-[24px]",
      },
      color: {
        positive: "",
        negative: "",
        notice: "",
        information: "",
        neutral: "",
        primary: "",
      },
      emphasis: {
        subtle: "",
        intense: "",
      },
    },
    compoundVariants: [
      // Positive - Subtle
      {
        color: "positive",
        emphasis: "subtle",
        class:
          "bg-feedback-fill-positive-subtle text-feedback-ink-positive-intense",
      },
      // Positive - Intense
      {
        color: "positive",
        emphasis: "intense",
        class:
          "bg-feedback-fill-positive-intense text-action-ink-on-primary-normal",
      },
      // Negative - Subtle
      {
        color: "negative",
        emphasis: "subtle",
        class:
          "bg-feedback-fill-negative-subtle text-feedback-ink-negative-subtle",
      },
      // Negative - Intense
      {
        color: "negative",
        emphasis: "intense",
        class:
          "bg-feedback-fill-negative-intense text-action-ink-on-primary-normal",
      },
      // Notice - Subtle
      {
        color: "notice",
        emphasis: "subtle",
        class: "bg-feedback-fill-notice-subtle text-feedback-ink-notice-subtle",
      },
      // Notice - Intense
      {
        color: "notice",
        emphasis: "intense",
        class:
          "bg-feedback-fill-notice-intense text-action-ink-on-primary-normal",
      },
      // Information - Subtle
      {
        color: "information",
        emphasis: "subtle",
        class: "bg-feedback-fill-info-subtle text-feedback-ink-info-subtle",
      },
      // Information - Intense
      {
        color: "information",
        emphasis: "intense",
        class:
          "bg-feedback-fill-info-intense text-action-ink-on-primary-normal",
      },
      // Neutral - Subtle
      {
        color: "neutral",
        emphasis: "subtle",
        class: "bg-surface-fill-neutral-subtle text-surface-ink-neutral-normal",
      },
      // Neutral - Intense
      {
        color: "neutral",
        emphasis: "intense",
        class:
          "bg-feedback-fill-neutral-intense text-action-ink-on-primary-normal",
      },
      // Primary - Subtle
      {
        color: "primary",
        emphasis: "subtle",
        class: "bg-surface-fill-primary-subtle text-surface-ink-primary-normal",
      },
      // Primary - Intense
      {
        color: "primary",
        emphasis: "intense",
        class:
          "bg-surface-fill-primary-intense text-action-ink-on-primary-normal",
      },
    ],
    defaultVariants: {
      size: "medium",
      color: "neutral",
      emphasis: "subtle",
    },
  }
);

type CounterVariantProps = VariantProps<typeof counterVariants>;
export type CounterSize = NonNullable<CounterVariantProps["size"]>;
export type CounterColor = NonNullable<CounterVariantProps["color"]>;
export type CounterEmphasis = NonNullable<CounterVariantProps["emphasis"]>;

export interface CounterProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  /**
   * The numerical value to display
   */
  value: number | string;
  /**
   * Maximum value to display before showing overflow indicator (e.g., "99+")
   */
  max?: number;
  /**
   * Custom class name for the counter
   */
  className?: string;
  /**
   * Size of the counter
   */
  size?: CounterSize;
  /**
   * Color variant of the counter
   */
  color?: CounterColor;
  /**
   * Visual emphasis level
   */
  emphasis?: CounterEmphasis;
}

const Counter = React.forwardRef<HTMLSpanElement, CounterProps>(
  (
    {
      value,
      max,
      size = "medium",
      color = "neutral",
      emphasis = "subtle",
      className,
      ...props
    },
    ref
  ) => {
    // Handle max value logic
    const displayValue = React.useMemo(() => {
      if (max !== undefined && typeof value === "number" && value > max) {
        return `${max}+`;
      }
      return value;
    }, [value, max]);

    return (
      <span
        ref={ref}
        className={cn(
          counterVariants({
            size,
            color,
            emphasis,
          }),
          className
        )}
        {...props}
      >
        {displayValue}
      </span>
    );
  }
);

Counter.displayName = "Counter";

export { Counter, counterVariants };
